<template>
  <div class="grow p-3 flex flex-col">
    <div class="flex flex-row flex-nowrap w-full border-b mb-2 relative">
      <FormKit
        v-model="newPresetName"
        label="Nieuw sjabloon"
        :ignore="true"
        type="text"
        placeholder="Naam sjabloon..."
        suffix-icon="add"
        @suffix-icon-click="saveCurrentStateAsPreset"
        @keydown.enter.prevent="saveCurrentStateAsPreset"
      />
    </div>
    <span class="font-bold text-sm">Opgeslagen profielen</span>
    <ul class="list-none">
      <li
        v-for="preset in presets"
        :key="preset.id"
        class="flex flex-row items-center border-2 mt-1 rounded hover:border-primary"
        :class="{ 'border-primary': isEditing(preset) }"
      >
        <input
          :id="`preset__${preset.id}`"
          :value="preset.name"
          class="p-3 block cursor-pointer grow bg-transparent focus:ring-0 focus:outline-none truncate"
          :readonly="!isEditing(preset)"
          @click.prevent="applyPreset(preset)"
          @blur="stopEditing()"
          @keydown.enter.prevent="
            (e: KeyboardEvent) =>
              savePreset({
                ...preset,
                name: (e.target as HTMLInputElement).value?.trim()
              })
          "
        >
        <AppIcon
          v-tooltip="'Bewerken'"
          icon="fa-edit"
          class="ml-auto hover:text-primary icon active:ring-0 active:outline-none"
          :class="{ 'text-primary': isEditing(preset) }"
          @click="editPreset(preset)"
        />
        <AppIcon
          v-tooltip="preset.default ? 'Verwijderen als standaard' : 'Instellen als standaard'"
          :icon="preset.default ? 'fa-house-circle-xmark' : 'fa-house'"
          class="ml-auto hover:text-primary icon active:ring-0 active:outline-none"
          @click="setDefaultPreset(preset)"
        />
        <AppIcon
          v-tooltip="preset.public ? 'Gedeeld. Klik om prive te maken' : 'Prive. Klik om publiek te maken'"
          :icon="preset.public ? 'fa-signal-stream' : 'fa-signal-stream-slash'"
          class="ml-auto hover:text-primary icon active:ring-0 active:outline-none"
          @click="setPublicPreset(preset)"
        />
        <AppIcon
          v-tooltip="'Verwijderen'"
          icon="fa-trash"
          class="hover:text-primary icon"
          @click="deletePreset(preset)"
        />
      </li>
    </ul>
    <p v-if="presets.length == 0">
      Er zijn nog geen profielen voor dit overzicht.
    </p>
  </div>
</template>

<script setup lang="ts">
import {
  UserGridPreset,
  createGridPreset,
  deleteGridPreset,
  getGridPresets,
  updateGridPreset,
  setDefaultGridPreset,
  shareGridPreset
} from "@q-food/q-food";
import { IToolPanelParams } from "ag-grid-community";
import { mapValues } from "lodash";
import { utcDate } from "~utils/Date.ts";
import { processFilterModel } from "~utils/Grids/GridProcessFilter.ts";

const newPresetName = ref("");
const presets = ref([] as UserGridPreset[]);
const editing = ref<string | null>(null);

const props = defineProps<{
  params: IToolPanelParams & { gridKey: string };
}>();

const { params } = toRefs(props);

onMounted(async () => {
  try {
    if (params.value.gridKey) {
      presets.value = await getGridPresets(params.value.gridKey);
    }
  } catch (error) {
    //
  }
});

function stopEditing() {
  editing.value = null;
}

function isEditing(preset: UserGridPreset) {
  return preset.id == editing.value;
}

function applyPreset(preset: UserGridPreset) {
  params.value.api.applyColumnState({ state: JSON.parse(preset.columnState), applyOrder: true });
  params.value.api.setFilterModel(processFilterModel(JSON.parse(preset.filterState)));
}

async function editPreset(preset: UserGridPreset) {
  editing.value = preset.id;
  document.getElementById(`preset__${preset.id}`)?.focus();
}

async function setDefaultPreset(preset: UserGridPreset) {
  await setDefaultGridPreset(preset.id);
  presets.value.forEach((p: UserGridPreset) => {
    if (preset.id === p.id) return;
    p.default = false;
  })
  preset.default = !preset.default;
}

async function setPublicPreset(preset: UserGridPreset) {
  await shareGridPreset(preset.id);
  preset.public = !preset.public;
}

function getFilterModel(): string {
  const filterModel = mapValues(params.value?.api.getFilterModel(), (filter) => {
    if (utcDate(filter.dateFrom).isSame(utcDate(), 'day')) {
      return {
        ...filter,
        dateFrom: "$today",
      }
    }

    return filter;
  });


  return JSON.stringify(filterModel);
}

async function savePreset(preset: UserGridPreset) {
  if (preset.name) {
    presets.value.find((p) => p.id == preset.id)!.name = preset.name;
    await updateGridPreset(preset.id, {
      gridKey: params.value?.gridKey,
      name: preset.name,
      columnState: JSON.stringify(params.value?.api.getColumnState()),
      filterState: getFilterModel(),
    });
  }
  stopEditing();
}

async function deletePreset(preset: UserGridPreset) {
  await deleteGridPreset(preset.id);

  const index = presets.value.indexOf(preset);
  presets.value.splice(index, 1);
}

async function saveCurrentStateAsPreset() {
  if (newPresetName.value.trim() == "") return;

  const newPreset = await createGridPreset({
    gridKey: params.value.gridKey,
    name: newPresetName.value.trim(),
    columnState: JSON.stringify(params.value?.api.getColumnState()),
    filterState: getFilterModel(),
  });
  newPresetName.value = "";
  presets.value.push(newPreset);
}
</script>

<style scoped>
.icon {
  @apply p-3 h-4 w-4 cursor-pointer;
}
</style>
